import {
  countWordsFromManuscriptEntry,
} from "./childrensBookManuscriptContent";

// Children’s Book physical-page capacity.
// Textbook and other Studios must define their own
// category-specific capacity profiles.
export const SINGLE_PAGE_REVIEW_WORD_LIMIT =
  350;

export const TWO_PAGE_SPREAD_REVIEW_WORD_LIMIT =
  SINGLE_PAGE_REVIEW_WORD_LIMIT * 2;

function clean(value) {
  return String(value ?? "").trim();
}


/*
 * Conservative writer-side approximation for shared
 * illustration/text pages.
 *
 * Production geometry remains authoritative for final layout.
 *
 * The current smallest supported production text box is
 * 486 points high. Using that geometry gives the most
 * conservative text share among the presently supported
 * trim sizes because the illustration/text gap is a fixed
 * point value:
 *
 * small illustration:
 *   486 - (486 * 0.25) - 9 = 355.5
 *
 * half-page illustration:
 *   486 - (486 * 0.50) - 12 = 231
 *
 * The caller must already have classified the Book Structure
 * unit as a text-bearing flow slot. This utility intentionally
 * does not decide skip or structural-barrier semantics.
 */
const CONSERVATIVE_SHARED_TEXT_BOX_HEIGHT_POINTS =
  486;

const SHARED_ILLUSTRATION_FLOW_POLICIES =
  Object.freeze({
    small_illustration:
      Object.freeze({
        visualHeightFraction:
          0.25,
        interRegionGapPoints:
          9,
      }),

    half_page:
      Object.freeze({
        visualHeightFraction:
          0.5,
        interRegionGapPoints:
          12,
      }),
  });


export function manuscriptFlowSlotWordLimit(
  unit = {},
  baseWordLimit =
    SINGLE_PAGE_REVIEW_WORD_LIMIT
) {
  const base =
    Number(
      baseWordLimit
    );

  if (
    !Number.isInteger(
      base
    ) ||
    base < 1
  ) {
    return null;
  }

  const illustrationPlan =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  const policy =
    SHARED_ILLUSTRATION_FLOW_POLICIES[
      illustrationPlan
    ];

  if (!policy) {
    return base;
  }

  const textHeightPoints =
    CONSERVATIVE_SHARED_TEXT_BOX_HEIGHT_POINTS -
    (
      CONSERVATIVE_SHARED_TEXT_BOX_HEIGHT_POINTS *
      policy.visualHeightFraction
    ) -
    policy.interRegionGapPoints;

  if (
    !Number.isFinite(
      textHeightPoints
    ) ||
    textHeightPoints <= 0
  ) {
    return null;
  }

  return Math.max(
    1,
    Math.floor(
      base *
      (
        textHeightPoints /
        CONSERVATIVE_SHARED_TEXT_BOX_HEIGHT_POINTS
      )
    )
  );
}


export function countManuscriptWords(
  value
) {
  const text = clean(value);

  return text
    ? text.split(/\s+/).length
    : 0;
}

export function manuscriptReviewWordLimit(
  unit = {}
) {
  return unit.unitType ===
    "double_page_spread"
    ? TWO_PAGE_SPREAD_REVIEW_WORD_LIMIT
    : SINGLE_PAGE_REVIEW_WORD_LIMIT;
}

export function reviewManuscriptUnitCapacity(
  unit = {},
  entry = {}
) {
  const wordCount =
    countWordsFromManuscriptEntry(
      entry
    );

  const wordLimit =
    manuscriptReviewWordLimit(
      unit
    );

  return {
    unitId: clean(unit.id),
    unitType:
      clean(unit.unitType) ||
      "single_page",
    wordCount,
    wordLimit,
    oversized:
      wordCount > wordLimit,
  };
}

export function findOversizedManuscriptUnits(
  projectPlan = {}
) {
  const units = Array.isArray(
    projectPlan.bookMap?.units
  )
    ? projectPlan.bookMap.units
    : [];

  const entries = Array.isArray(
    projectPlan.manuscriptPlan
      ?.entries
  )
    ? projectPlan.manuscriptPlan
        .entries
    : [];

  const entriesByUnit =
    entries.reduce(
      (result, entry) => {
        const unitId =
          clean(entry?.unitId);

        if (unitId) {
          result[unitId] = entry;
        }

        return result;
      },
      {}
    );

  return units
    .map((unit) => ({
      unit,
      entry:
        entriesByUnit[
          clean(unit?.id)
        ] || {},
      capacity:
        reviewManuscriptUnitCapacity(
          unit,
          entriesByUnit[
            clean(unit?.id)
          ] || {}
        ),
    }))
    .filter(
      ({ capacity }) =>
        capacity.oversized
    );
}


// Open-Book C2A:
//
// Publication Proof represents bounded physical pages.
// Its readiness check therefore needs the conservative
// physical text share already used by manuscript flow.
//
// This is deliberately separate from
// reviewManuscriptUnitCapacity(). The established shared
// review behaviour remains unchanged for Reader View,
// readiness, handoff and other existing consumers.
//
// Explicit full_spread is deliberately absent. It retains
// its distinct cross-gutter authority and the established
// two-page review limit.
const PUBLICATION_PROOF_ONE_PAGE_ILLUSTRATION_PLANS =
  new Set([
    "small_illustration",
    "half_page",
    "full_page",
  ]);


export function publicationProofManuscriptWordLimit(
  unit = {}
) {
  const unitType =
    clean(
      unit?.unitType
    ).toLowerCase();

  const illustrationPlan =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  if (
    unitType ===
      "double_page_spread"
  ) {
    /*
     * Ordinary illustrated spreads in Publication Proof
     * give one physical page to the illustration and one
     * physical page to manuscript text.
     *
     * full_spread remains exempt because it has deliberate
     * cross-gutter composition authority.
     */
    return PUBLICATION_PROOF_ONE_PAGE_ILLUSTRATION_PLANS
      .has(
        illustrationPlan
      )
      ? SINGLE_PAGE_REVIEW_WORD_LIMIT
      : TWO_PAGE_SPREAD_REVIEW_WORD_LIMIT;
  }

  /*
   * Single-page small and half-page illustration layouts
   * reuse the conservative image-aware text capacity that
   * already exists for manuscript flow.
   *
   * Unknown or non-shared layouts retain the established
   * Single Page review limit.
   */
  const sharedLimit =
    manuscriptFlowSlotWordLimit(
      unit,
      SINGLE_PAGE_REVIEW_WORD_LIMIT
    );

  return (
    Number.isInteger(
      sharedLimit
    ) &&
    sharedLimit > 0
  )
    ? sharedLimit
    : SINGLE_PAGE_REVIEW_WORD_LIMIT;
}


export function reviewPublicationProofManuscriptUnitCapacity(
  unit = {},
  entry = {}
) {
  const wordCount =
    countWordsFromManuscriptEntry(
      entry
    );

  const wordLimit =
    publicationProofManuscriptWordLimit(
      unit
    );

  return {
    unitId:
      clean(
        unit.id
      ),

    unitType:
      clean(
        unit.unitType
      ) ||
      "single_page",

    wordCount,
    wordLimit,

    oversized:
      wordCount >
      wordLimit,
  };
}


export function findPublicationProofOversizedManuscriptUnits(
  projectPlan = {}
) {
  const units =
    Array.isArray(
      projectPlan.bookMap
        ?.units
    )
      ? projectPlan.bookMap
          .units
      : [];

  const entries =
    Array.isArray(
      projectPlan.manuscriptPlan
        ?.entries
    )
      ? projectPlan.manuscriptPlan
          .entries
      : [];

  const entriesByUnit =
    entries.reduce(
      (
        result,
        entry
      ) => {
        const unitId =
          clean(
            entry?.unitId
          );

        if (unitId) {
          result[
            unitId
          ] = entry;
        }

        return result;
      },
      {}
    );

  return units
    .map(
      (unit) => ({
        unit,

        entry:
          entriesByUnit[
            clean(
              unit?.id
            )
          ] || {},

        capacity:
          reviewPublicationProofManuscriptUnitCapacity(
            unit,
            entriesByUnit[
              clean(
                unit?.id
              )
            ] || {}
          ),
      })
    )
    .filter(
      ({
        capacity,
      }) =>
        capacity
          .oversized
    );
}
